import {
  mapExploreCollectionRow,
  mapCollectionCardMarkets,
  mapCollectionCardMultiMarkets,
  resolveFeedFeaturedMultiMarket,
  sortCollectionRowsByVolume,
  hasCollectionMarkets,
} from '@/domain/market/collection-card-map.js';
import { getFirstListedMarket } from '@/utils/market-list-status.js';
import { MULTI_COLLECTION_CARD_OUTCOME_MAX } from '@/constants/multi-outcome-ui.js';
import {
  isCryptoUpdownSlug,
  resolveFeedCardCollectionSlug,
} from '@/domain/crypto/crypto-minute-slug.js';
import { formatMinuteUpdownDisplayTitle } from '@/domain/crypto/crypto-updown-title-split.js';
import { resolveViewingUpdownRound } from '@/domain/crypto/crypto-updown-series.js';

const UPDOWN_TITLE_RE = /up\s*(?:or|\/)\s*down|updown|涨还是跌|漲還是跌/i;

/** 卡片与详情共用盘口时间窗，再按现有 ET 口径格式化。 */
function applyUpdownFeedCardTitle(card, collectionSlug, locale, row) {
  const slug = String(collectionSlug ?? '').trim();
  const title = String(card?.title ?? '').trim();
  if (!title) return;
  const hasKnownWindow = isCryptoUpdownSlug(slug);
  if (!hasKnownWindow && !UPDOWN_TITLE_RE.test(title)) return;

  // Title-only matches do not establish a period; preserve their source suffix.
  const viewing = hasKnownWindow ? resolveViewingUpdownRound(row, slug) : {};
  const formatted = formatMinuteUpdownDisplayTitle(title, slug, { ...viewing, locale });
  if (formatted.title) card.title = formatted.title;
  if (formatted.subtitle) card.titleSubtitle = formatted.subtitle;
}

function formatCompactNum(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(Math.round(num));
}

export function formatVolumeValue(n) {
  return `$${formatCompactNum(n)}`;
}

/** Domain：平台无关 ViewModel，不含 i18n（categoryKey / 原始 outcome 文案） */
export function mapFeedCardFromRow(row, locale = 'zh') {
  const categoryKey = String(row.category || '').trim();
  const card = mapExploreCollectionRow(row, {
    formatVolumeValue,
    categoryLabel: (raw) => String(raw ?? '').trim(),
  });
  if (!card.id) return null;

  if (!hasCollectionMarkets(row)) {
    const collectionSlug = resolveFeedCardCollectionSlug(row);
    return {
      ...card,
      categoryKey,
      collectionSlug,
      showUpdownFlow: false,
      variant: 'empty',
      marketRows: [],
      multiMarketRows: [],
      multiOptions: [],
      yesPercent: 0,
      noPercent: 0,
      yesLabel: '',
      noLabel: '',
      outcomeValues: [],
      outcomesRaw: undefined,
    };
  }

  const market = getFirstListedMarket(row?.markets);
  card.categoryKey = categoryKey;
  card.marketCount =
    row.marketPreview === true ? Number(row.marketCount) : (row.markets || []).length;
  card.outcomesRaw =
    market?.outcomes ?? (card.outcomeValues?.length >= 2 ? card.outcomeValues : undefined);
  card.marketRows = mapCollectionCardMarkets(row, locale);
  card.multiMarketRows = mapCollectionCardMultiMarkets(row, locale);

  const featuredMulti = resolveFeedFeaturedMultiMarket(row, {
    maxOutcomes: MULTI_COLLECTION_CARD_OUTCOME_MAX,
  });
  if (featuredMulti && card.variant !== 'complete') {
    card.variant = 'multi';
    card.multiOptions = featuredMulti.multiOptions;
    card.outcomeValues = featuredMulti.outcomeValues;
    card.orderSymbol = featuredMulti.orderSymbol || card.orderSymbol;
    if (featuredMulti.tradingCollateral) {
      card.tradingCollateral = featuredMulti.tradingCollateral;
    }
    if (featuredMulti.exchangeAddress) {
      card.exchangeAddress = featuredMulti.exchangeAddress;
    }
    card.multiMarketRows = [];
    card.collectionSlug = resolveFeedCardCollectionSlug(row);
    card.showUpdownFlow = false;
    return card;
  }

  if (card.variant !== 'complete') {
    if (card.marketRows.length > 1) {
      card.variant = 'binary';
    } else if (card.multiMarketRows.length === 1 && !card.multiOptions.length) {
      const only = card.multiMarketRows[0];
      card.multiOptions = only.multiOptions || [];
      card.outcomeValues = only.outcomeValues || [];
      if (only.tradingCollateral) card.tradingCollateral = only.tradingCollateral;
      if (only.exchangeAddress) card.exchangeAddress = only.exchangeAddress;
      card.variant = 'multi';
    }
  }

  const collectionSlug = resolveFeedCardCollectionSlug(row);
  card.collectionSlug = collectionSlug;
  applyUpdownFeedCardTitle(card, collectionSlug, locale, row);
  card.showUpdownFlow = false;

  return card;
}

export function mapFeedCards(apiRows, options = {}) {
  const rows = options.sortByVolume ? sortCollectionRowsByVolume(apiRows) : apiRows;
  const locale = options.locale || 'zh';
  return rows.map((row) => mapFeedCardFromRow(row, locale)).filter(Boolean);
}

export function feedCardItemKey(card) {
  return String(card?.id ?? '').trim();
}
